/**
 * LUMEN — main application orchestrator
 * Everything is deliberately kept in a few focused modules.
 * The experience is local-first and delightful by itself.
 * Contribution to the commons is explicit, one-line-consent, Turnstile-protected.
 */

import type { SceneFamily, DeviceState, ProbeRecord } from './types';
import { SARTSession, computeLapseIndex } from './sart';
import { ClearingScene } from './scenes';
import { ClearingAudio } from './audio';
import { saveProbe, saveExposure, computePersonalStats, exportAllAsCSV, clearAllData } from './storage';
import { computeLumen } from './metrics';
import { setProbeStatus, showRecoveryButton, updateLumenSummary, renderHistoryChart, showTurnstileMessage } from './ui';
import { registerDevice, submitProbe, submitExposure, submitRestoration, getCommons, forget, renderTurnstile, setApiBase } from './api';

// ---------- Device identity (anonymous, stable, device-keyed) ----------
function getOrCreateDeviceKey(): string {
  const KEY = 'lumen-device-key';
  let key = localStorage.getItem(KEY);
  if (!key) {
    key = crypto.randomUUID();
    localStorage.setItem(KEY, key);
  }
  return key;
}

let DEVICE_KEY = getOrCreateDeviceKey();
let deviceState: DeviceState = {
  deviceKey: DEVICE_KEY,
  consentGiven: false,
  localOnly: false,
};

// ---------- Turnstile site key (user must replace for real contributions) ----------
const TURNSTILE_SITEKEY = (document.querySelector('meta[name="turnstile"]') as any)?.content ||
  '0x4AAAAAAA-your-public-sitekey-here'; // <-- REPLACE with your real site key

// ---------- State ----------
let lastPreProbe: ProbeRecord | null = null;
let lastExposureId: number | null = null;
let currentScene: ClearingScene | null = null;
let currentAudio: ClearingAudio | null = null;
let currentSceneFamily: SceneFamily | null = null;
let lastSessionLumen = 0;

// ---------- Boot ----------
async function boot() {
  // Make sure we have a device row on the backend when consent is given later
  await registerDevice(DEVICE_KEY, navigator.language, new Date().getTimezoneOffset());

  setupNav();
  setupConsent();
  setupProbeButtons();
  setupSceneCards();
  setupScenePlayerControls();
  setupContribute();
  setupCommons();
  setupForget();
  setupExport();

  // Initial personal view
  await refreshPersonalView();

  // Friendly first-run hint
  setTimeout(() => {
    const banner = document.getElementById('consent-banner');
    if (banner && !deviceState.consentGiven) {
      banner.classList.add('ring-1', 'ring-lumen-400/40');
    }
  }, 2200);

  // Dev convenience: allow setting API base via ?api=https://...
  const params = new URLSearchParams(location.search);
  if (params.get('api')) setApiBase(params.get('api')!);
}

function setupNav() {
  document.getElementById('nav-science')?.addEventListener('click', () => {
    const el = document.createElement('div');
    el.className = 'fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6';
    el.innerHTML = `
      <div class="max-w-2xl bg-forest-900 border border-white/10 rounded-3xl p-8 text-sm">
        <h3 class="text-2xl font-semibold tracking-tight mb-3">The Science</h3>
        <div class="space-y-4 text-forest-200">
          <p><strong>Attention Restoration Theory</strong> (Kaplan, 1995) proposes that directed (effortful) attention fatigues, while certain environments engage “soft fascination” that allows it to recover without effort.</p>
          <p class="science-quote">“Soft fascination… permits a person to think about other things while attending to the pattern.” — Kaplan</p>
          <p>The <strong>SART</strong> (Sustained Attention to Response Task, Robertson et al. 1997) is a well-validated probe of sustained attention and mind-wandering. Commission errors and RT variability (CV) are the key signals.</p>
          <p>LUMEN measures <em>objective change</em> in these signals before vs after a brief, fully procedural micro-environment. The resulting <strong>Lumen</strong> is your personal restoration responsiveness for that experience.</p>
          <p class="text-xs text-forest-400 pt-2">No questionnaires replace the probe. Self-report ratings are collected as additional ART-aligned dimensions only.</p>
        </div>
        <button class="mt-6 px-5 py-2 rounded-2xl border border-white/20 text-xs" onclick="this.closest('.fixed').remove()">Close</button>
      </div>`;
    document.body.appendChild(el);
    el.addEventListener('click', (e) => { if (e.target === el) el.remove(); });
  });

  document.getElementById('nav-your-lumen')?.addEventListener('click', () => {
    document.getElementById('section-your-lumen')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  document.getElementById('nav-commons')?.addEventListener('click', () => {
    document.getElementById('section-commons')?.scrollIntoView({ behavior: 'smooth' });
  });
}

function setupConsent() {
  const banner = document.getElementById('consent-banner')!;
  const accept = document.getElementById('consent-accept')!;
  const localOnly = document.getElementById('consent-local-only')!;

  accept.addEventListener('click', () => {
    deviceState.consentGiven = true;
    deviceState.localOnly = false;
    banner.style.transition = 'opacity .2s';
    banner.style.opacity = '0';
    setTimeout(() => banner.remove(), 180);
    // Gentle nudge to run first probe
    setProbeStatus('Ready. Run a baseline probe to begin measuring your attention.');
  });

  localOnly.addEventListener('click', () => {
    deviceState.consentGiven = false;
    deviceState.localOnly = true;
    banner.style.transition = 'opacity .2s';
    banner.style.opacity = '0';
    setTimeout(() => {
      banner.innerHTML = `<div class="text-xs text-forest-300">Running locally. All data stays in this browser. You can contribute later from “Your Lumen”.</div>`;
      banner.style.opacity = '1';
    }, 120);
  });
}

function setupProbeButtons() {
  const baselineBtn = document.getElementById('start-baseline')!;
  const recoveryBtn = document.getElementById('start-recovery')!;

  baselineBtn.addEventListener('click', () => runProbe('pre'));
  recoveryBtn.addEventListener('click', () => runProbe('post'));
}

async function runProbe(phase: 'pre' | 'post') {
  const canvas = document.getElementById('sart-canvas') as HTMLCanvasElement;
  const overlay = document.getElementById('sart-overlay')!;
  const live = document.getElementById('sart-live')!;

  setProbeStatus(phase === 'pre' ? 'Running baseline… stay with the task.' : 'Running recovery probe…');
  overlay.classList.add('hidden');
  live.classList.remove('hidden');

  const session = new SARTSession(
    canvas,
    async (result) => {
      session.destroy();

      const record: ProbeRecord = {
        phase,
        sceneFamily: phase === 'post' ? currentSceneFamily : null,
        result,
        ts: Date.now(),
      };

      // Persist locally (always)
      const localId = await saveProbe(record);

      if (phase === 'pre') {
        lastPreProbe = { ...record, id: localId };
        showRecoveryButton(false);
        setProbeStatus('Baseline captured. Now choose a clearing and spend 2–4 minutes inside it.');
        // Enable scene cards
        document.querySelectorAll('.scene-card').forEach(c => (c as HTMLElement).classList.remove('opacity-50', 'pointer-events-none'));
      } else {
        // post
        if (lastPreProbe && currentSceneFamily) {
          const delta = computeLumen(lastPreProbe.result, result);
          lastSessionLumen = delta.lumen;

          // Show nice immediate feedback
          setProbeStatus(`Recovery complete. Your Lumen for this session: ${delta.lumen}. Lower variability = clearer attention returned.`);

          // Optionally store restoration link locally (we send it on contribution)
          // For the personal view we recompute everything from IDB
          await refreshPersonalView();

          // Enable contribute
          (document.getElementById('contribute-btn') as HTMLButtonElement).disabled = false;
        }
        showRecoveryButton(false);
        // Reset scene state
        exitScene();
      }

      // Re-show instructions overlay
      setTimeout(() => {
        overlay.classList.remove('hidden');
        live.classList.add('hidden');
        const instr = document.getElementById('sart-instructions');
        if (instr) instr.textContent = phase === 'pre' 
          ? 'Baseline done. Enter a clearing next.' 
          : 'Recovery probe complete. Check “Your Lumen”.';
      }, 900);
    },
    (trial, errors) => {
      // live updates already handled inside SART, but we can augment here if wanted
    }
  );

  // Start
  session.start();

  if (phase === 'post') {
    showRecoveryButton(false);
  }
}

function setupSceneCards() {
  document.querySelectorAll('.scene-card').forEach(card => {
    const fam = (card as HTMLElement).dataset.scene as SceneFamily;
    card.addEventListener('click', () => enterScene(fam));
  });
}

function enterScene(family: SceneFamily) {
  const player = document.getElementById('scene-player')!;
  const canvas = document.getElementById('scene-canvas') as HTMLCanvasElement;
  const nameEl = document.getElementById('scene-name')!;

  player.classList.remove('hidden');
  player.scrollIntoView({ block: 'center', behavior: 'smooth' });

  // Cleanup previous
  if (currentScene) currentScene.destroy();
  if (currentAudio) currentAudio.stop();

  currentSceneFamily = family;
  nameEl.textContent = family === 'canopy' ? 'Canopy' : family === 'ripples' ? 'Ripples' : 'Meadow';

  currentScene = new ClearingScene(family, canvas);
  currentAudio = new ClearingAudio();

  // Start audio (user gesture friendly — we are in a click handler)
  currentAudio.start().then(() => {
    currentAudio!.setIntensity(0.85);
    currentAudio!.setVolume(0.65);
  });

  // Wire live controls
  const intensity = document.getElementById('scene-intensity') as HTMLInputElement;
  const vol = document.getElementById('audio-volume') as HTMLInputElement;
  const muteBtn = document.getElementById('audio-toggle')!;

  intensity.oninput = () => {
    const v = parseFloat(intensity.value);
    currentScene?.setIntensity(v);
    currentAudio?.setIntensity(v);
  };
  vol.oninput = () => currentAudio?.setVolume(parseFloat(vol.value));
  muteBtn.onclick = () => {
    const m = currentAudio?.toggleMute();
    muteBtn.textContent = m ? 'Unmute' : 'Mute';
  };

  // Spatial coupling (mouse already inside scene; add tilt update loop)
  const spatialLoop = setInterval(() => {
    if (!currentScene || !currentAudio) return;
    // The scene already reads mouse; we just feed normalized values to audio
    currentAudio.updateSpatial(
      (currentScene as any).params?.mouseX ?? 0.5,
      (currentScene as any).params?.tiltX ?? 0
    );
  }, 180);

  // Auto-exit after generous time + gentle prompt
  const start = Date.now();
  const timerEl = document.getElementById('scene-timer')!;
  const timer = setInterval(() => {
    const s = Math.floor((Date.now() - start) / 1000);
    timerEl.textContent = `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    if (s > 420) { // ~7 min hard cap
      clearInterval(timer);
      clearInterval(spatialLoop);
      exitScene();
    }
  }, 1000);

  // Store timer id for cleanup
  (player as any)._timer = timer;
  (player as any)._spatial = spatialLoop;

  // Immediately after entering, we can allow recovery probe only after they exit with ratings
  showRecoveryButton(false);
}

function setupScenePlayerControls() {
  const exit = document.getElementById('scene-exit')!;
  const immersive = document.getElementById('scene-immersive')!;
  const finish = document.getElementById('finish-clearing')!;

  exit.addEventListener('click', () => exitScene());

  immersive.addEventListener('click', async () => {
    const canvas = document.getElementById('scene-canvas') as HTMLCanvasElement;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await canvas.requestFullscreen?.();
      }
      // Wake Lock (Chrome)
      // @ts-ignore
      if ('wakeLock' in navigator) {
        // @ts-ignore
        await navigator.wakeLock.request('screen');
      }
    } catch {}
  });

  finish.addEventListener('click', async () => {
    if (!currentSceneFamily) return;

    const ratings = {
      fascination: parseInt((document.getElementById('rate-fascination') as HTMLInputElement).value),
      beingAway: parseInt((document.getElementById('rate-away') as HTMLInputElement).value),
      extent: parseInt((document.getElementById('rate-extent') as HTMLInputElement).value),
      compatibility: parseInt((document.getElementById('rate-compat') as HTMLInputElement).value),
    };

    const reflection = (document.getElementById('public-reflection') as HTMLInputElement).value.trim() || undefined;

    // Save exposure locally
    const expId = await saveExposure({
      sceneFamily: currentSceneFamily,
      durationS: Math.floor((Date.now() - ((document.getElementById('scene-player') as any)._start || Date.now())) / 1000),
      ratings,
      publicReflection: reflection,
      ts: Date.now(),
    });
    lastExposureId = expId as number;

    // Enable recovery probe
    showRecoveryButton(true);
    setProbeStatus('Ratings recorded. Run the recovery probe now to measure what changed.');

    exitScene({ keepPlayerVisible: true });
  });
}

function exitScene(opts: { keepPlayerVisible?: boolean } = {}) {
  const player = document.getElementById('scene-player')!;
  const timer = (player as any)._timer;
  const spatial = (player as any)._spatial;
  if (timer) clearInterval(timer);
  if (spatial) clearInterval(spatial);

  if (currentScene) {
    currentScene.destroy();
    currentScene = null;
  }
  if (currentAudio) {
    currentAudio.stop();
    currentAudio = null;
  }

  if (!opts.keepPlayerVisible) {
    player.classList.add('hidden');
  }
  currentSceneFamily = null;
}

function setupContribute() {
  const btn = document.getElementById('contribute-btn') as HTMLButtonElement;
  const allBtn = document.getElementById('contribute-all-btn') as HTMLButtonElement;
  const status = document.getElementById('contribute-status')!;
  const tsContainer = document.getElementById('turnstile-container')!;

  btn.disabled = true;

  async function doContribute(allHistory = false) {
    if (deviceState.localOnly) {
      status.textContent = 'You chose local-only mode. Change consent in the banner or refresh to contribute.';
      return;
    }
    if (!TURNSTILE_SITEKEY || TURNSTILE_SITEKEY.includes('your-public')) {
      status.textContent = 'Turnstile site key not configured. See README. Contributions disabled until set.';
      return;
    }

    status.textContent = 'Preparing contribution…';

    // Render (or re-render) Turnstile
    tsContainer.innerHTML = '';
    renderTurnstile(tsContainer, TURNSTILE_SITEKEY, async (token) => {
      try {
        status.textContent = 'Verifying and sending…';

        // Always send the last session if we have it
        if (lastPreProbe && lastExposureId) {
          // We need to submit pre, exposure, post, restoration.
          // For simplicity in this MVP we submit the most recent post as the recovery and link.
          // In a fuller version we would store the exact post id from the last run.

          // Re-fetch latest probes to get the real post
          const allProbes = await (await import('./storage')).getAllProbes(); // dynamic to avoid circular
          const posts = allProbes.filter(p => p.phase === 'post').sort((a, b) => b.ts - a.ts);
          const latestPost = posts[0];
          const pres = allProbes.filter(p => p.phase === 'pre' && p.ts < latestPost.ts).sort((a, b) => b.ts - a.ts);
          const latestPre = pres[0];

          if (latestPre && latestPost) {
            // Submit pre + post
            const preRes: any = await submitProbe({
              device_key: DEVICE_KEY,
              turnstileToken: token,
              phase: 'pre',
              scene_family: latestPre.sceneFamily,
              ...latestPre.result,
              duration_ms: latestPre.result.durationMs,
              interrupted: latestPre.result.interrupted,
            });

            const postRes: any = await submitProbe({
              device_key: DEVICE_KEY,
              turnstileToken: token,
              phase: 'post',
              scene_family: latestPost.sceneFamily,
              ...latestPost.result,
              duration_ms: latestPost.result.durationMs,
              interrupted: latestPost.result.interrupted,
            });

            // Exposure (we already saved locally; send again with same data)
            const exposures = await (await import('./storage')).getAllExposures();
            const lastExp = exposures.find(e => e.id === lastExposureId) || exposures[exposures.length - 1];

            const expRes: any = await submitExposure({
              device_key: DEVICE_KEY,
              turnstileToken: token,
              scene_family: lastExp.sceneFamily,
              duration_s: lastExp.durationS,
              ratings: {
                fascination: lastExp.ratings.fascination,
                being_away: lastExp.ratings.beingAway,
                extent: lastExp.ratings.extent,
                compatibility: lastExp.ratings.compatibility,
              },
              public_reflection: lastExp.publicReflection,
            });

            const delta = computeLumen(latestPre.result, latestPost.result);

            await submitRestoration({
              device_key: DEVICE_KEY,
              turnstileToken: token,
              exposure_id: expRes.exposure_id || lastExposureId || 0,
              pre_id: preRes.probe_id || 0,
              post_id: postRes.probe_id || 0,
              deltas: {
                delta_commission: delta.deltaCommission,
                delta_cv: delta.deltaCV,
                delta_mean_rt: delta.deltaMeanRT,
              },
              lumen: delta.lumen,
            });
          }
        }

        if (allHistory) {
          // For a fuller history contribution we could batch more, but one rich session + stats is already high signal.
          status.textContent = 'Thank you. Your signature is now part of the living map.';
        } else {
          status.textContent = 'Thank you. One rich, measured session added to the commons.';
        }

        // Refresh commons for everyone
        setTimeout(() => loadCommons(), 650);
      } catch (e: any) {
        console.error(e);
        status.textContent = 'Something went wrong sending data. Your local copy is safe.';
      }
    });
  }

  btn.addEventListener('click', () => doContribute(false));
  allBtn.addEventListener('click', () => doContribute(true));
}

async function setupCommons() {
  // Load on boot and after contributions
  await loadCommons();
}

async function loadCommons() {
  const famEl = document.getElementById('commons-families')!;
  const resEl = document.getElementById('commons-resonances')!;

  try {
    // If we have a recent public reflection from this browser, try to use its embedding for resonance
    const exposures = await (await import('./storage')).getAllExposures();
    const withReflection = exposures.filter(e => e.publicReflection).slice(-1)[0];
    let emb: number[] | undefined;

    // We don't store the embedding locally (it's generated server-side), so we just call without for now.
    // In a future iteration we could embed client-side with ONNX or call the AI gateway, but for free tier the server does it.

    const data: any = await getCommons(emb);

    // Families
    famEl.innerHTML = '';
    if (data?.family_averages?.length) {
      for (const row of data.family_averages) {
        const d = document.createElement('div');
        d.className = 'flex justify-between text-sm border-b border-white/10 py-1';
        d.innerHTML = `
          <div>${row.scene_family}</div>
          <div class="tabular-nums text-forest-300">${row.avg_fasc} fasc · ${row.n} sessions</div>
        `;
        famEl.appendChild(d);
      }
    } else {
      famEl.innerHTML = `<div class="text-xs text-forest-300">No public data yet. Be the first to contribute a session.</div>`;
    }

    // Resonances
    resEl.innerHTML = '';
    if (data?.resonances?.length) {
      for (const r of data.resonances) {
        const d = document.createElement('div');
        d.className = 'border-l-2 border-lumen-400/50 pl-3';
        d.innerHTML = `
          <div class="text-xs text-lumen-400">${r.scene_family} · fascination ${r.fascination}</div>
          <div class="text-forest-100 italic">“${r.reflection}”</div>
        `;
        resEl.appendChild(d);
      }
    } else {
      resEl.innerHTML = `<div class="text-xs text-forest-300">Reflections from similar states will appear here after people contribute.</div>`;
    }
  } catch (e) {
    famEl.innerHTML = `<div class="text-xs text-forest-300">Commons temporarily unreachable (still 100% usable locally).</div>`;
  }
}

function setupForget() {
  const btn = document.getElementById('nav-forget')!;
  btn.addEventListener('click', async () => {
    if (!confirm('This will permanently delete ALL your local data and (if previously contributed) request deletion from the server. Continue?')) return;

    // Local first
    await clearAllData();
    localStorage.removeItem('lumen-device-key');
    DEVICE_KEY = getOrCreateDeviceKey();

    // Try server delete (needs a Turnstile token)
    const status = document.getElementById('contribute-status')!;
    const tsContainer = document.getElementById('turnstile-container')!;

    if (TURNSTILE_SITEKEY && !TURNSTILE_SITEKEY.includes('your-public')) {
      tsContainer.innerHTML = '';
      renderTurnstile(tsContainer, TURNSTILE_SITEKEY, async (token) => {
        try {
          await forget(DEVICE_KEY, token);
          status.textContent = 'Server copy also deleted (or never existed).';
        } catch {
          status.textContent = 'Local data cleared. Server deletion may have failed or had no rows.';
        }
      });
    } else {
      status.textContent = 'Local data cleared. (No Turnstile key — server side may still have previous rows.)';
    }

    // Refresh UI
    await refreshPersonalView();
    location.reload();
  });
}

function setupExport() {
  document.getElementById('export-csv')?.addEventListener('click', async () => {
    const csv = await exportAllAsCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `lumen-my-data-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  });
}

async function refreshPersonalView() {
  const stats = await computePersonalStats();
  updateLumenSummary({
    sessions: stats.sessions,
    avgDeltaCV: stats.avgDeltaCV,
    bestScene: stats.bestScene,
    bestDelta: stats.bestDelta,
  });

  const chartContainer = document.getElementById('history-chart')!;
  renderHistoryChart(chartContainer, stats.history);
}

// Kick off
boot().catch(console.error);

// Expose a tiny debug hook for power users (console)
(window as any).LUMEN_DEBUG = {
  getDeviceKey: () => DEVICE_KEY,
  recompute: () => (window as any).location.reload(),
  clear: async () => { await clearAllData(); location.reload(); },
};
